import { useEffect, useMemo, useRef, useState } from 'react';

import { canonicalRouteFor, homeForLanguage, loadSearchIndex, locales, normaliseRoute, pageForKnowledgeId, pages, translationFor } from './data';
import { ArrowIcon, ArrowUpRightIcon, CloseIcon, GlobeIcon, MenuIcon, SearchIcon } from './icons';
import { Link, useNavigation } from './router';
import { BrandMark as SiteSymbol } from './BrandMark';
import type { SearchRecord, SiteLanguage, SitePageSummary } from './types';

export function BrandMark({ compact = false, href = '/' }: { compact?: boolean; href?: string }) {
  return (
    <Link href={href} className={`brand ${compact ? 'brand--compact' : ''}`} aria-label="bottomsurgery.info home">
      <SiteSymbol className="brand__mark" />
      {!compact && <span className="brand__wordmark">bottomsurgery<span>.info</span></span>}
    </Link>
  );
}

const labels = {
  en: { explore: 'Explore', collections: 'Collections', compare: 'Compare', stories: 'Stories', tools: 'Tools', search: 'Search' },
  es: { explore: 'Explorar', collections: 'Colecciones', compare: 'Comparar', stories: 'Historias', tools: 'Herramientas', search: 'Buscar' },
};

function route(language: SiteLanguage, knowledgeId: string, fallback: string) {
  return pageForKnowledgeId(language, knowledgeId)?.route ?? fallback;
}

function navigationFor(language: SiteLanguage) {
  const copy = language === 'es' ? labels.es : labels.en;
  return [
    { label: copy.explore, href: route(language, 'explorer', language === 'es' ? '/es/explorar' : '/explore') },
    { label: copy.collections, href: `${route(language, 'explorer', language === 'es' ? '/es/explorar' : '/explore')}#collections` },
    { label: copy.compare, href: route(language, 'compare', language === 'es' ? '/es/comparar' : '/compare') },
    { label: copy.stories, href: route(language, 'collection-stories', language === 'es' ? '/es/historias' : '/stories') },
    { label: copy.tools, href: route(language, 'collection-tools', language === 'es' ? '/es/herramientas' : '/tools') },
  ];
}

export function SiteHeader({ page, searchOpen, onSearch }: { page: SitePageSummary | null; searchOpen: boolean; onSearch: () => void }) {
  const language = page?.lang === 'es' ? 'es' : 'en';
  const copy = labels[language];
  const { navigate, path } = useNavigation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigation = navigationFor(language);
  const homeRoute = canonicalRouteFor(homeForLanguage(language));

  useEffect(() => setMenuOpen(false), [path]);

  const menuRef = useRef<HTMLButtonElement>(null);
  const openSearch = () => {
    setMenuOpen(false);
    onSearch();
  };
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setMenuOpen(false); menuRef.current?.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  useEffect(() => {
    if (searchOpen) setMenuOpen(false);
  }, [searchOpen]);

  const chooseLanguage = (targetLanguage: string) => {
    const target = page ? translationFor(page, targetLanguage) : homeForLanguage(targetLanguage);
    navigate(canonicalRouteFor(target ?? homeForLanguage(targetLanguage)));
  };

  return (
    <header className="site-header">
      <div className="site-header__inner shell">
        <BrandMark href={homeRoute} />
        <nav className="desktop-nav" aria-label="Primary navigation">
          {navigation.map((item) => <Link key={item.label} href={item.href} aria-current={!item.href.includes('#') && normaliseRoute(item.href) === path ? 'page' : undefined}>{item.label}</Link>)}
        </nav>
        <div className="site-header__actions">
          <button className="header-search" type="button" onClick={openSearch} aria-label={copy.search} aria-haspopup="dialog" aria-expanded={searchOpen} aria-controls="site-search-dialog">
            <SearchIcon /><span>{copy.search}</span><kbd>⌘K</kbd>
          </button>
          <label className="language-link" aria-label="Language">
            <GlobeIcon />
            <select aria-label="Language" value={language} onChange={(event) => chooseLanguage(event.target.value)}>
              {locales.map((locale) => <option key={locale.code} value={locale.code}>{locale.nativeName}</option>)}
            </select>
          </label>
          <button ref={menuRef} className="menu-button" type="button" onClick={() => setMenuOpen((open) => !open)} aria-expanded={menuOpen} aria-controls="mobile-navigation" aria-label={menuOpen ? (language === 'es' ? 'Cerrar menú' : 'Close menu') : (language === 'es' ? 'Abrir menú' : 'Open menu')}>
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>
      <div id="mobile-navigation" className={`mobile-nav ${menuOpen ? 'mobile-nav--open' : ''}`}>
        <nav className="shell" aria-label="Mobile navigation">
          {navigation.map((item) => <Link key={item.label} href={item.href} aria-current={!item.href.includes('#') && normaliseRoute(item.href) === path ? 'page' : undefined}>{item.label}<ArrowIcon /></Link>)}
        </nav>
      </div>
    </header>
  );
}

function normaliseSearch(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
}

function scorePage(page: SitePageSummary, query: string, bodyText = '') {
  const words = normaliseSearch(query).split(/\s+/).filter(Boolean);
  const title = normaliseSearch(page.title);
  const description = normaliseSearch(page.description);
  const body = normaliseSearch(bodyText);
  let score = 0;
  for (const word of words) {
    if (title === word) score += 50;
    else if (title.startsWith(word)) score += 25;
    else if (title.includes(word)) score += 15;
    if (description.includes(word)) score += 6;
    if (body.includes(word)) score += 2;
  }
  if (title.includes(normaliseSearch(query))) score += 30;
  return score;
}

export function SearchDialog({ open, language, onClose, loadIndex = loadSearchIndex }: { open: boolean; language: SiteLanguage; onClose: () => void; loadIndex?: () => Promise<SearchRecord[]> }) {
  const [query, setQuery] = useState('');
  const [searchTextById, setSearchTextById] = useState<Record<string, string>>({});
  const [searchLoadState, setSearchLoadState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [activeResult, setActiveResult] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const resultRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const { navigate } = useNavigation();

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveResult(-1);
    const returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    let cancelled = false;
    setSearchLoadState('loading');
    loadIndex().then((records) => {
      if (!cancelled) {
        setSearchTextById(Object.fromEntries(records.map((record) => [record.id, record.text])));
        setSearchLoadState('ready');
      }
    }).catch(() => {
      if (!cancelled) setSearchLoadState('error');
    });
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'Tab' && dialogRef.current) {
        const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>('input, button, [href], select, [tabindex]:not([tabindex="-1"])')].filter((element) => !element.hasAttribute('disabled'));
        const first = focusable[0];
        const last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    document.documentElement.classList.add('dialog-open');
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKey);
      document.documentElement.classList.remove('dialog-open');
      returnFocus?.focus();
    };
  }, [loadAttempt, loadIndex, open, onClose]);

  const articlePages = useMemo(() => pages.filter((page) => page.wordCount > 0), []);
  const results = useMemo(() => {
    if (!query.trim()) return articlePages.filter((page) => page.lang === language).slice(0, 9);
    return articlePages
      .map((page) => ({ page, score: scorePage(page, query, searchTextById[page.id]) + (page.lang === language ? 3 : 0) }))
      .filter((result) => result.score > 0)
      .sort((a, b) => b.score - a.score || a.page.title.localeCompare(b.page.title))
      .slice(0, 12)
      .map((result) => result.page);
  }, [articlePages, language, query, searchTextById]);

  if (!open) return null;
  const go = (target: string) => { navigate(target); onClose(); };
  const spanish = language === 'es';
  const moveResult = (index: number) => {
    if (!results.length) return;
    const next = (index + results.length) % results.length;
    setActiveResult(next);
    resultRefs.current[next]?.focus();
  };

  return (
    <div className="search-layer" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section id="site-search-dialog" ref={dialogRef} className="search-dialog" role="dialog" aria-modal="true" aria-labelledby="search-title">
        <div className="search-dialog__input-row">
          <SearchIcon />
          <label id="search-title" className="sr-only" htmlFor="site-search">{spanish ? 'Buscar en bottomsurgery.info' : 'Search bottomsurgery.info'}</label>
          <input ref={inputRef} id="site-search" type="search" value={query} onChange={(event) => { setQuery(event.target.value); setActiveResult(-1); }} onKeyDown={(event) => { if (event.key === 'ArrowDown') { event.preventDefault(); moveResult(0); } }} placeholder={spanish ? 'Procedimiento, recuperación, seguro…' : 'Procedure, recovery, insurance…'} autoComplete="off" />
          <button ref={closeRef} type="button" onClick={onClose} aria-label={spanish ? 'Cerrar búsqueda' : 'Close search'}><CloseIcon /></button>
        </div>
        <div className="search-dialog__meta" aria-live="polite" aria-atomic="true"><span>{searchLoadState === 'loading' ? (spanish ? 'Cargando búsqueda de texto completo…' : 'Loading full-text search…') : query ? `${results.length} ${spanish ? 'resultados' : 'results'}` : spanish ? 'Buscar en el texto completo' : 'Search the full text'}</span><kbd>esc</kbd></div>
        {searchLoadState === 'error' && <div className="search-load-error" role="status" aria-live="polite"><strong>{spanish ? 'No se pudo cargar la búsqueda de texto completo.' : 'Full-text search could not load.'}</strong><button type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>{spanish ? 'Reintentar índice de búsqueda' : 'Retry search index'}</button></div>}
        <div className="search-results" role="listbox" aria-label={spanish ? 'Resultados de búsqueda' : 'Search results'}>
          {results.map((result, index) => (
            <button ref={(element) => { resultRefs.current[index] = element; }} key={result.id} type="button" role="option" aria-selected={activeResult === index} onFocus={() => setActiveResult(index)} onKeyDown={(event) => { if (event.key === 'ArrowDown') { event.preventDefault(); moveResult(index + 1); } else if (event.key === 'ArrowUp') { event.preventDefault(); if (index === 0) inputRef.current?.focus(); else moveResult(index - 1); } }} onClick={() => go(result.route)}>
              <span className="search-result__type">{result.collection}</span>
              <strong>{result.title}</strong>
              <span className="search-result__description">{result.description}</span>
              <ArrowIcon />
            </button>
          ))}
          {!results.length && <div className="search-empty"><strong>{spanish ? 'No encontramos ese tema.' : 'We could not find that topic.'}</strong><span>{spanish ? 'Prueba una palabra más amplia.' : 'Try a broader term.'}</span></div>}
        </div>
      </section>
    </div>
  );
}

export function SiteFooter({ language }: { language: SiteLanguage }) {
  const spanish = language === 'es';
  const homeRoute = canonicalRouteFor(homeForLanguage(language));
  const links = navigationFor(language);
  return (
    <footer className="site-footer">
      <div className="shell site-footer__grid">
        <div className="site-footer__brand"><BrandMark href={homeRoute} /><p>{spanish ? 'Creado desde la experiencia vivida. Gratuito para quien venga después.' : 'Built from lived experience. Kept free for the next person.'}</p></div>
        <nav aria-label={spanish ? 'Explorar el sitio' : 'Explore the site'}>{links.map((item) => <Link key={item.label} href={item.href}>{item.label}</Link>)}</nav>
        <div className="footer-support">
          <span>{spanish ? 'Guía descargable' : 'Downloadable guide'}</span>
          <Link href={route(language, 'about-this-guide-download', language === 'es' ? '/es/historias/sobre-esta-guia-y-descarga' : '/stories/about-this-guide-download')}>Becoming Yourself</Link>
          <a href="https://www.gumroad.com/products/tyslr" target="_blank" rel="noopener noreferrer">{spanish ? 'Apoyar el proyecto' : 'Support the project'}<ArrowUpRightIcon /></a>
        </div>
      </div>
      <div className="shell site-footer__fineprint"><p>{spanish ? 'Educación comunitaria, no asesoramiento médico individual.' : 'Community education, not individualized medical advice.'}</p><p>© {new Date().getUTCFullYear()} bottomsurgery.info</p></div>
    </footer>
  );
}

export function ReadingProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const update = () => {
      const height = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(height > 0 ? Math.min(1, window.scrollY / height) : 0);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => { window.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, []);
  return <div className="reading-progress" aria-hidden="true"><span style={{ transform: `scaleX(${progress})` }} /></div>;
}
